import { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { Users, Receipt, TrendingUp, DollarSign, Search, Plus, Minus, Loader2, ShieldCheck, Coins, Sparkles, AlertOctagon, ShieldOff, Shield } from "lucide-react";
import { toast } from "sonner";
import { useAuth, formatApiError } from "@/contexts/AuthContext";
import { useLang } from "@/i18n";
import { api } from "@/lib/api";
import AdminContentModule from "@/components/AdminContentModule";

function StatCard({ icon: Icon, label, value, sub }) {
  return (
    <div className="bg-neutral-950 border border-neutral-800 rounded-sm p-6">
      <div className="flex items-center gap-2 text-neutral-500">
        <Icon size={16} className="text-red-500" />
        <span className="eyebrow">{label}</span>
      </div>
      <p className="headline text-3xl text-white mt-2">{value}</p>
      {sub && <p className="text-xs font-mono text-neutral-500 mt-1">{sub}</p>}
    </div>
  );
}

export default function Admin() {
  const { user, loading } = useAuth();
  const { lang } = useLang();
  const [tab, setTab] = useState("users");
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [txns, setTxns] = useState([]);
  const [ledger, setLedger] = useState([]);
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);

  const loadAll = async () => {
    setBusy(true);
    try {
      const [s, u, t, l] = await Promise.all([
        api.get("/admin/stats").then(r => r.data),
        api.get("/admin/users", { params: { limit: 200, search: search || undefined } }).then(r => r.data),
        api.get("/admin/transactions", { params: { limit: 100 } }).then(r => r.data),
        api.get("/admin/ledger", { params: { limit: 200 } }).then(r => r.data),
      ]);
      setStats(s); setUsers(u); setTxns(t); setLedger(l);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (user?.is_admin) loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const adjustCredits = async (userId, delta) => {
    const val = prompt(lang === "tr" ? `Kredi değişimi (pozitif ekler, negatif düşer):` : `Credit delta (positive=add, negative=subtract):`, String(delta));
    if (val === null) return;
    const n = parseInt(val, 10);
    if (isNaN(n) || n === 0) return;
    try {
      await api.post(`/admin/users/${userId}/credits`, { delta: n, reason: "admin_adjust" });
      toast.success(lang === "tr" ? "Kredi güncellendi" : "Credits updated");
      loadAll();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  if (loading) return <div className="py-32 flex items-center justify-center"><Loader2 className="animate-spin text-red-500" size={32} /></div>;
  if (!user) return <Navigate to="/login?redirect=/admin" replace />;
  if (!user.is_admin) {
    return (
      <div className="max-w-md mx-auto px-4 py-32 text-center" data-testid="admin-forbidden">
        <ShieldCheck size={48} className="text-red-500 mx-auto" />
        <h1 className="headline text-3xl text-white mt-4">{lang === "tr" ? "Yetki yok" : "Access denied"}</h1>
        <p className="mt-2 text-neutral-400">{lang === "tr" ? "Bu sayfa yalnızca yöneticiler içindir." : "This area is admin-only."}</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="admin-page">
      <div className="flex items-center gap-3 mb-8">
        <ShieldCheck className="text-red-500" size={28} />
        <div>
          <span className="eyebrow text-red-500">ADMIN CONSOLE · loft.press</span>
          <h1 className="headline text-4xl sm:text-5xl text-white mt-1">{lang === "tr" ? "Yönetim Paneli" : "Admin Panel"}</h1>
        </div>
        <div className="ml-auto">
          <Link
            to="/admin/control-center"
            className="flex items-center gap-1.5 px-4 py-2 border border-neutral-700 text-neutral-300 hover:border-red-600 hover:text-red-400 rounded-sm text-xs font-bold uppercase tracking-wider transition-colors"
            data-testid="admin-control-center-link"
          >
            <Shield size={14}/> Denetim Merkezi
          </Link>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-10">
        <StatCard icon={Users} label={lang === "tr" ? "Toplam Kullanıcı" : "Total Users"} value={stats?.users_total ?? "—"} />
        <StatCard icon={TrendingUp} label={lang === "tr" ? "Ödeyen Kullanıcı" : "Paying Users"} value={stats?.users_paying ?? "—"} />
        <StatCard icon={Receipt} label={lang === "tr" ? "Toplam İşlem" : "Total Txns"} value={stats?.transactions_total ?? "—"} />
        <StatCard icon={Coins} label={lang === "tr" ? "Aktif Abonelik" : "Active Subs"} value={stats?.subscriptions_active ?? "—"} />
        <StatCard icon={DollarSign} label={lang === "tr" ? "Gelir (USD)" : "Revenue (USD)"} value={`$${(stats?.revenue_usd ?? 0).toLocaleString()}`} />
      </div>

      {/* Tabs */}
      <div className="border-b border-neutral-800 mb-6 flex gap-1">
        {[
          ["users", lang === "tr" ? "Kullanıcılar" : "Users"],
          ["transactions", lang === "tr" ? "İşlemler" : "Transactions"],
          ["ledger", lang === "tr" ? "Kredi Defteri" : "Credit Ledger"],
          ["refunds", lang === "tr" ? "İadeler & Chargeback" : "Refunds & Chargeback"],
          ["content", "YENİ API SYSTEM"],
        ].map(([id, label]) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            className={`px-4 py-3 text-sm font-bold uppercase tracking-wider border-b-2 transition-colors ${
              tab === id ? "border-red-500 text-red-500" : "border-transparent text-neutral-500 hover:text-white"
            }`}
            data-testid={`admin-tab-${id}`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "users" && (
        <div>
          <div className="flex gap-2 mb-4">
            <div className="relative flex-1">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && loadAll()}
                placeholder={lang === "tr" ? "E-posta ara..." : "Search email..."}
                className="w-full bg-neutral-950 border border-neutral-800 text-white rounded-sm pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-600"
                data-testid="admin-user-search"
              />
            </div>
            <button onClick={loadAll} className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-sm uppercase text-xs font-bold tracking-wider">{busy ? "..." : (lang === "tr" ? "Ara" : "Search")}</button>
          </div>
          <div className="bg-neutral-950 border border-neutral-800 rounded-sm overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-neutral-900 text-neutral-400 text-[10px] uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-3">Email</th>
                  <th className="text-left px-4 py-3">Name</th>
                  <th className="text-left px-4 py-3">Plan</th>
                  <th className="text-right px-4 py-3">Credits</th>
                  <th className="text-left px-4 py-3">Created</th>
                  <th className="text-center px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-800">
                {users.map((u) => (
                  <tr key={u.id} className="hover:bg-neutral-900/50" data-testid={`admin-user-${u.id}`}>
                    <td className="px-4 py-3 font-mono text-neutral-300">{u.email} {u.is_admin && <span className="ml-1 px-1.5 py-0.5 text-[9px] bg-red-600 text-white rounded-sm">ADMIN</span>}</td>
                    <td className="px-4 py-3 text-white">{u.name || "—"}</td>
                    <td className="px-4 py-3 uppercase text-xs font-bold text-red-400">{u.plan}</td>
                    <td className="px-4 py-3 text-right font-mono text-white">{u.credits.toLocaleString()}</td>
                    <td className="px-4 py-3 font-mono text-neutral-500 text-xs">{new Date(u.created_at).toLocaleDateString()}</td>
                    <td className="px-4 py-3 text-center">
                      <button onClick={() => adjustCredits(u.id, 1000)} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-sm bg-neutral-900 hover:bg-green-950 hover:text-green-400 border border-neutral-800 text-neutral-300" data-testid={`admin-credit-adjust-${u.id}`}>
                        <Plus size={12} /> {lang === "tr" ? "Düzenle" : "Adjust"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "transactions" && (
        <div className="bg-neutral-950 border border-neutral-800 rounded-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-neutral-900 text-neutral-400 text-[10px] uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3">Txn ID</th>
                <th className="text-left px-4 py-3">User</th>
                <th className="text-right px-4 py-3">Amount</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-800">
              {txns.length === 0 && (
                <tr><td colSpan="5" className="px-4 py-12 text-center text-neutral-500">{lang === "tr" ? "Henüz işlem yok" : "No transactions yet"}</td></tr>
              )}
              {txns.map((t) => (
                <tr key={t.id} className="hover:bg-neutral-900/50" data-testid={`admin-txn-${t.id}`}>
                  <td className="px-4 py-3 font-mono text-xs text-neutral-400">{t.id.slice(0, 20)}...</td>
                  <td className="px-4 py-3 font-mono text-neutral-300">{t.user_id ? t.user_id.slice(0, 8) + "..." : "—"}</td>
                  <td className="px-4 py-3 text-right font-mono text-white">
                    {t.grand_total ? `$${(parseInt(t.grand_total) / 100).toFixed(2)}` : "—"} {t.currency}
                  </td>
                  <td className="px-4 py-3 uppercase text-xs font-bold text-green-400">{t.status}</td>
                  <td className="px-4 py-3 font-mono text-neutral-500 text-xs">{t.completed_at ? new Date(t.completed_at).toLocaleString() : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "content" && <AdminContentModule />}

      {tab === "refunds" && <RefundsPanel lang={lang} />}

      {tab === "ledger" && (
        <div className="bg-neutral-950 border border-neutral-800 rounded-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-neutral-900 text-neutral-400 text-[10px] uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3">User</th>
                <th className="text-left px-4 py-3">Reason</th>
                <th className="text-right px-4 py-3">Delta</th>
                <th className="text-right px-4 py-3">Balance After</th>
                <th className="text-left px-4 py-3">Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-800">
              {ledger.map((l, i) => (
                <tr key={i} className="hover:bg-neutral-900/50">
                  <td className="px-4 py-3 font-mono text-xs text-neutral-400">{l.user_id?.slice(0, 8)}...</td>
                  <td className="px-4 py-3 text-neutral-300">{l.reason}</td>
                  <td className={`px-4 py-3 text-right font-mono ${l.delta > 0 ? "text-green-400" : "text-red-400"}`}>{l.delta > 0 ? "+" : ""}{l.delta.toLocaleString()}</td>
                  <td className="px-4 py-3 text-right font-mono text-white">{l.balance_after.toLocaleString()}</td>
                  <td className="px-4 py-3 font-mono text-neutral-500 text-xs">{new Date(l.created_at).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ==================================================================
// Faz 7: Refunds & Chargeback Review Panel
// ==================================================================
function RefundsPanel({ lang }) {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [reviewing, setReviewing] = useState(null); // refund doc
  const [decision, setDecision] = useState("no_action");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (filter === "chargeback" || filter === "refund" || filter === "credit") params.action = filter;
      if (filter === "pending") params.review_state = "chargeback_review";
      if (filter === "resolved") params.review_state = "resolved";
      const { data } = await api.get("/admin/refunds", { params });
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const submit = async () => {
    if (!reviewing) return;
    setBusy(true);
    try {
      await api.post(`/admin/refunds/${reviewing.id}/resolve`, { decision, note: note || undefined });
      toast.success(lang === "tr" ? "İnceleme kapatıldı" : "Review closed");
      setReviewing(null);
      setNote("");
      setDecision("no_action");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const badge = (r) => {
    if (r.review_state === "resolved") return <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-sm border bg-neutral-900 border-neutral-700 text-neutral-300">RESOLVED</span>;
    if (r.action === "chargeback") return <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-sm border bg-red-950/60 border-red-800 text-red-300"><AlertOctagon size={11}/> CHARGEBACK</span>;
    return <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-sm border bg-yellow-950/60 border-yellow-800 text-yellow-300">REFUND</span>;
  };

  return (
    <div className="space-y-4" data-testid="refunds-panel">
      <div className="flex items-center gap-2 flex-wrap">
        <ShieldOff size={18} className="text-red-500"/>
        <h3 className="headline text-xl text-white">{lang === "tr" ? "İadeler & Chargeback" : "Refunds & Chargeback"}</h3>
        <div className="flex-1"/>
        {[
          ["", lang === "tr" ? "Hepsi" : "All"],
          ["pending", lang === "tr" ? "İnceleme Bekliyor" : "Pending"],
          ["resolved", lang === "tr" ? "Kapanan" : "Resolved"],
          ["refund", "Refund"],
          ["chargeback", "Chargeback"],
        ].map(([val, label]) => (
          <button key={val || "all"} onClick={() => setFilter(val)}
                  className={`px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider rounded-sm border transition-colors ${
                    filter === val ? "bg-red-600 border-red-600 text-white"
                                   : "bg-neutral-950 border-neutral-800 text-neutral-400 hover:text-white"
                  }`}
                  data-testid={`refunds-filter-${val || "all"}`}>{label}</button>
        ))}
      </div>

      <p className="text-xs text-neutral-500 leading-relaxed max-w-3xl">
        {lang === "tr"
          ? "Paddle webhook'undan gelen refund ve chargeback olayları burada listelenir. Kredi geri alma otomatik yapılır (bakiye asla negatife düşmez), ancak hesap askıya alma yalnızca senin manuel inceleme kararınla uygulanır."
          : "Refund and chargeback events from Paddle webhooks are listed here. Credit clawback is automatic (balance never goes negative), but account suspension is applied ONLY after your manual review decision."}
      </p>

      <div className="bg-neutral-950 border border-neutral-800 rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-neutral-900 text-neutral-400 text-[10px] uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-3">Tür</th>
              <th className="text-left px-4 py-3">Adjustment</th>
              <th className="text-left px-4 py-3">User</th>
              <th className="text-right px-4 py-3">Tutar</th>
              <th className="text-right px-4 py-3">Kredi (İstenen / Geri Alınan)</th>
              <th className="text-left px-4 py-3">Durum</th>
              <th className="text-left px-4 py-3">Tarih</th>
              <th className="text-right px-4 py-3">İşlem</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-800">
            {loading && items.length === 0 && (
              <tr><td colSpan="8" className="px-4 py-12 text-center"><Loader2 className="animate-spin text-red-500 inline"/></td></tr>
            )}
            {!loading && items.length === 0 && (
              <tr><td colSpan="8" className="px-4 py-16 text-center text-neutral-500">{lang === "tr" ? "Henüz iade/chargeback kaydı yok." : "No refund/chargeback records yet."}</td></tr>
            )}
            {items.map((r) => (
              <tr key={r.id} className="hover:bg-neutral-900/50" data-testid={`refund-row-${r.id}`}>
                <td className="px-4 py-3">{badge(r)}</td>
                <td className="px-4 py-3 font-mono text-[10px] text-neutral-400">{r.adjustment_id || r.id}</td>
                <td className="px-4 py-3 font-mono text-xs text-neutral-300">{(r.user_id || "").slice(0, 8) || "—"}</td>
                <td className="px-4 py-3 text-right font-mono text-white text-xs">${(r.amount_usd || 0).toFixed(2)} {r.currency || ""}</td>
                <td className="px-4 py-3 text-right font-mono text-xs">
                  <span className="text-neutral-400">{(r.credits_requested || 0).toLocaleString()}</span>
                  <span className="mx-1 text-neutral-600">/</span>
                  <span className={r.partial_recovered ? "text-yellow-400" : "text-emerald-400"}>{(r.credits_recovered || 0).toLocaleString()}</span>
                  {r.partial_recovered && <span className="ml-1 text-[9px] text-yellow-500">⚠ partial</span>}
                </td>
                <td className="px-4 py-3">
                  {r.review_decision && (
                    <span className="text-[10px] uppercase tracking-wider font-bold text-neutral-300">
                      {r.review_decision === "suspend_account" ? (lang === "tr" ? "Askıya alındı" : "Suspended") :
                       r.review_decision === "restore_credits" ? (lang === "tr" ? "Kredi iade" : "Credits restored") :
                       (lang === "tr" ? "Aksiyon yok" : "No action")}
                    </span>
                  )}
                  {!r.review_decision && (
                    <span className="text-[10px] uppercase tracking-wider font-bold text-yellow-400">
                      {lang === "tr" ? "İnceleme bekliyor" : "Pending review"}
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 font-mono text-[10px] text-neutral-500">{new Date(r.received_at).toLocaleString(lang === "tr" ? "tr-TR" : "en-US")}</td>
                <td className="px-4 py-3 text-right">
                  {r.review_state !== "resolved" ? (
                    <button onClick={() => { setReviewing(r); setDecision("no_action"); setNote(""); }}
                            className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider bg-red-600 hover:bg-red-700 text-white rounded-sm"
                            data-testid={`refund-review-${r.id}`}>
                      {lang === "tr" ? "İncele" : "Review"}
                    </button>
                  ) : (
                    <span className="text-[10px] text-neutral-500">{r.reviewed_by}</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Review dialog (native modal-lite) */}
      {reviewing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => !busy && setReviewing(null)}>
          <div className="bg-neutral-950 border border-neutral-800 rounded-sm p-6 max-w-lg w-full space-y-4" onClick={(e) => e.stopPropagation()} data-testid="refund-review-dialog">
            <div>
              <p className="eyebrow text-neutral-500 mb-1">{reviewing.action === "chargeback" ? "CHARGEBACK REVIEW" : "REFUND REVIEW"}</p>
              <h4 className="headline text-2xl text-white">{lang === "tr" ? "İnceleme Kararı" : "Review Decision"}</h4>
              <p className="text-xs text-neutral-500 mt-1 font-mono">{reviewing.adjustment_id} · ${reviewing.amount_usd?.toFixed(2)} {reviewing.currency}</p>
            </div>
            <div className="text-xs text-neutral-400 bg-neutral-900 border border-neutral-800 rounded-sm p-3 space-y-1">
              <p><span className="text-neutral-500">Kullanıcı:</span> <span className="font-mono">{reviewing.user_id}</span></p>
              <p><span className="text-neutral-500">Kredi geri alınan:</span> {(reviewing.credits_recovered || 0).toLocaleString()} / {(reviewing.credits_requested || 0).toLocaleString()}
                {reviewing.partial_recovered && <span className="text-yellow-400 ml-2">⚠ kısmi (bakiye 0&apos;a sabitlendi)</span>}
              </p>
              <p><span className="text-neutral-500">Sebep:</span> {reviewing.reason || "—"}</p>
            </div>
            <div className="space-y-2">
              <p className="text-[11px] font-bold uppercase tracking-wider text-neutral-400">{lang === "tr" ? "Karar" : "Decision"}</p>
              {[
                ["no_action", lang === "tr" ? "Aksiyon yok — sadece kayıt" : "No action — record only"],
                ["restore_credits", lang === "tr" ? `Kredileri iade et (+${(reviewing.credits_recovered || 0).toLocaleString()})` : `Restore credits (+${(reviewing.credits_recovered || 0).toLocaleString()})`],
                ["suspend_account", lang === "tr" ? "Hesabı askıya al (API erişimi kapanır)" : "Suspend account (API access blocked)"],
              ].map(([val, label]) => (
                <label key={val} className={`flex items-center gap-2 p-3 rounded-sm border cursor-pointer transition-colors ${decision === val ? "bg-red-950/30 border-red-700" : "bg-neutral-900 border-neutral-800 hover:border-neutral-600"}`} data-testid={`refund-decision-${val}`}>
                  <input type="radio" checked={decision === val} onChange={() => setDecision(val)} className="accent-red-500"/>
                  <span className="text-sm text-white">{label}</span>
                </label>
              ))}
            </div>
            <div>
              <p className="text-[11px] font-bold uppercase tracking-wider text-neutral-400 mb-1">{lang === "tr" ? "Not (opsiyonel)" : "Note (optional)"}</p>
              <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)}
                        placeholder={lang === "tr" ? "İnceleme notunu buraya yaz…" : "Type your review note…"}
                        className="w-full bg-neutral-900 border border-neutral-800 text-white rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-600"
                        data-testid="refund-review-note"/>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setReviewing(null)} disabled={busy}
                      className="px-4 py-2 border border-neutral-800 text-neutral-300 rounded-sm uppercase text-xs font-bold tracking-wider hover:bg-neutral-900">
                {lang === "tr" ? "İptal" : "Cancel"}
              </button>
              <button onClick={submit} disabled={busy}
                      className="px-5 py-2 bg-red-600 hover:bg-red-700 text-white rounded-sm uppercase text-xs font-bold tracking-wider inline-flex items-center gap-2 disabled:opacity-60"
                      data-testid="refund-review-submit">
                {busy && <Loader2 size={14} className="animate-spin"/>}
                {lang === "tr" ? "Kaydet" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

